import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Mail, CheckCircle, XCircle, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export function DPATracker() {
  const [dpas, setDpas] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDPA, setSelectedDPA] = useState<any>(null);

  useEffect(() => {
    fetchDPAs();
  }, []);

  const fetchDPAs = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("dpa_tracking")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load DPA tracking data");
      setIsLoading(false);
      return;
    }

    setDpas(data || []);
    setIsLoading(false);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: any; icon: any; label: string }> = {
      pending: { variant: "secondary", icon: Clock, label: "Pending" },
      sent: { variant: "default", icon: Mail, label: "Sent" },
      signed: { variant: "default", icon: CheckCircle, label: "Signed" },
      expired: { variant: "destructive", icon: AlertTriangle, label: "Expired" },
      rejected: { variant: "destructive", icon: XCircle, label: "Rejected" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getRiskBadge = (risk: string) => {
    const colors: Record<string, string> = {
      low: "bg-green-100 text-green-800",
      medium: "bg-yellow-100 text-yellow-800",
      high: "bg-orange-100 text-orange-800",
      critical: "bg-red-100 text-red-800",
    };
    return (
      <Badge className={colors[risk] || colors.medium}>
        {risk.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>DPA Tracking</CardTitle>
              <CardDescription>
                Track Data Processing Agreements with all vendors
              </CardDescription>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Vendor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Vendor DPA</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vendor-name">Vendor Name</Label>
                      <Input id="vendor-name" placeholder="Vendor Inc." />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-email">Contact Email</Label>
                      <Input id="contact-email" type="email" placeholder="legal@vendor.com" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-person">Contact Person</Label>
                      <Input id="contact-person" placeholder="John Doe" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input id="location" placeholder="US/EU" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="data-processed">Data Processed</Label>
                    <Textarea 
                      id="data-processed" 
                      placeholder="Describe what data this vendor processes..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="risk-level">Risk Level</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select risk level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select defaultValue="pending">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="signed">Signed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea id="notes" placeholder="Additional notes..." />
                  </div>
                  <Button className="w-full">Add Vendor</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Data Processed</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Risk Level</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Loading DPA tracking data...
                    </TableCell>
                  </TableRow>
                ) : dpas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No vendor DPAs tracked yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  dpas.map((dpa) => (
                    <TableRow key={dpa.id}>
                      <TableCell className="font-medium">{dpa.vendor_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {dpa.contact_person && <div>{dpa.contact_person}</div>}
                          {dpa.contact_email && (
                            <div className="text-muted-foreground">{dpa.contact_email}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{dpa.data_processed}</TableCell>
                      <TableCell>{dpa.location}</TableCell>
                      <TableCell>{getRiskBadge(dpa.risk_level)}</TableCell>
                      <TableCell>{getStatusBadge(dpa.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {dpa.sent_date && (
                            <div className="text-muted-foreground">
                              Sent: {format(new Date(dpa.sent_date), "dd MMM yyyy")}
                            </div>
                          )}
                          {dpa.signed_date && (
                            <div className="text-green-600">
                              Signed: {format(new Date(dpa.signed_date), "dd MMM yyyy")}
                            </div>
                          )}
                          {dpa.expiry_date && (
                            <div className="text-orange-600">
                              Expires: {format(new Date(dpa.expiry_date), "dd MMM yyyy")}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setSelectedDPA(dpa)}
                          >
                            View
                          </Button>
                          <Button size="sm" variant="ghost">
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
